/**
 * @fileoverview PDB data parsing and formatting
 * Does not yet parse CONECT lines.
 *
 * @typedef {{ resKey: string, atomName: string, altLoc: string }} AtomLookup
 *
 * @typedef {{
 *   atomNum: number,
 *   atomName: string,
 *   altLoc: string,
 *   x: number,
 *   y: number,
 *   z: number,
 *   occupancy: number,
 *   tempFactor: number,
 *   element: string,
 *   charge: string,
 *   connections: Array<AtomLookup>,
 * }} PdbAtomSpec
 *
 * @typedef {{
 *   hetflag: boolean,
 *   resName: string,
 *   seqNum: number,
 *   iCode: string,
 *   chainId: string,
 *   atoms: Array<PdbAtomSpec>,
 * }} PdbResidueSpec
 *
 * @typedef {{ remarks: Object.<number, string | string[]> }} PdbHeaderSpec
 *
 * @typedef {{
 *   header: PdbHeaderSpec,
 *   residues: Array<PdbResidueSpec>,
 * }} PdbDataSpec
 */

export function formResidueKey(resn, resi, chain, { iCode, altLoc, modelNum }={}) {
    let suffix = '';
    if (iCode) suffix += iCode;
    if (altLoc) {
        if (!iCode) suffix += '_';
        suffix += altLoc;
    }
    const model = (modelNum && modelNum !== 0) ? `#${modelNum}` : '';
    return `${resn}.${resi}${suffix}:${chain}${model}`;
}

export class PdbData {
    constructor(input) {
        this.header = new PdbHeader();
        this.residues = [];
        this.chainInfo = {};

        if (typeof input === 'string') {
            const { residues, chainInfo, parseErrors } = PdbData.parsePdbString(input);
            if (parseErrors?.length > 0) {
                throw new Error(`PDB parsing error: ${parseErrors.join('\n')}`);
            }
            this.residues = residues;
            this.chainInfo = chainInfo;
            this.originalPdbString = input;
        } else if (typeof input === 'object') {
            if (input.header) {
                this.header = new PdbHeader(input.header);
            }
            if (input.residues) {
                const { residues, chainMap } = PdbData.fixResidues(input.residues);
                this.residues = residues.map((residue) => new PdbResidue(residue));
                for (const residue of this.residues) {
                    const currentChainId = residue.chainId;
                    if (!this.chainInfo[currentChainId]) {
                        this.chainInfo[currentChainId] = { residues: [], terKey: null };
                        // Find and assign originalChainId if different,
                        // for multi-character chains adjusted after passed in programmatically.
                        const [originalChainId] = [...chainMap.entries()].find(
                            ([, val]) => val === currentChainId
                        ) || [];
                        if (originalChainId !== currentChainId) {
                            this.chainInfo[currentChainId].originalChainId = originalChainId;
                        }
                    }
                    this.chainInfo[currentChainId].residues.push(residue);
                }
            }
        }
    }

    get atoms() {
        return this.residues.flatMap((residue) => residue.atoms);
    }

    get hasH() {
        return this.residues.some((residue) => residue.hasH);
    }

    lookupOriginalChain(currentChainId) {
        const entry = this.chainInfo[currentChainId];
        if (entry) {
            return entry.originalChainId || currentChainId;
        } else {
            return entry;
        }
    }

    /**
     * @param {{
     * hetflag?: boolean, isWater?: boolean, isSingle?: boolean, hasH?: boolean
     * }} filterProps
     */
    filterResidues(filterProps={}) {
        function check(residue) {
            return Object.entries(filterProps).every(([key, value]) => (
                typeof value === 'function' ? value(residue) : residue[key] === value
            ));
        }
        return this.residues.filter((check));
    }

    lookupAtom({ resKey, atomName, altLoc }) {
        const residues = this.residues.filter((res) => res.resKey === resKey);
        const found = residues.map((res) => res.getAtom({ atomName, altLoc })).filter(Boolean);
        if (found.length > 1) {
            console.warn(`Multiple atoms found for ${resKey} ${atomName}${altLoc ? ` (${altLoc})` : ''}`);
        }
        return found[0];
    }

    static parsePdbString(pdbString) {
        const lines = pdbString.replaceAll('\r', '').split('\n');
        const chainInfo = {};
        const residues = {};
        const parseErrors = [];
        function ensureChainEntry(chainId) {
            if (!chainInfo[chainId]) {
                chainInfo[chainId] = { residues: [], terKey: null };
            }
        }
        for (const [lineNum, line] of lines.entries()) {
            const recordName = line.slice(0, 6).trim();
            switch (recordName) {
                case 'ATOM':
                case 'HETATM': {
                    const atomRaw = parsePdbAtomLine(line);
                    if (atomRaw) {
                        const resKey = atomRaw.resKey;
                        if (!residues[resKey]) {
                            residues[resKey] = new PdbResidue(atomRaw);
                            ensureChainEntry(atomRaw.chainId);
                            chainInfo[atomRaw.chainId].residues.push(residues[resKey]);
                        } else {
                            residues[resKey].addAtom(atomRaw);
                        }
                    } else {
                        parseErrors.push(`Line ${lineNum + 1}: Invalid ATOM/HETATM line: ${line}`);
                    }
                    break;
                }
                case 'TER': {
                    const terRaw = parsePdbTerLine(line);
                    ensureChainEntry(terRaw.chainId);
                    chainInfo[terRaw.chainId].terKey = terRaw.resKey;
                    break;
                }
                default:
                    if (recordName.match(/\s/)) {
                        parseErrors.push(`Invalid pdb record name: ${recordName}`);
                    }
                    break;
            }
        }
        return { residues: Object.values(residues), chainInfo, parseErrors };
    }

    format(options={}) {
        const parts = [
            this.header.format(),
            this.formatCoordinatesAndConnections(options),
            `${'END'.padEnd(80, ' ')}\n`,
        ].filter(Boolean);
        return parts.join('\n');
    }

    formatCoordinatesAndConnections(options={}) {
        const parts = [];
        let counter = 1;
        const atomMap = new Map();

        let lastResidue;
        const maybeAddTer = (currRes) => {
            if (!lastResidue) return;
            const terKey = this.chainInfo[lastResidue.chainId]?.terKey;
            if (terKey) {
                if (lastResidue.resKey === terKey) {
                    parts.push(lastResidue.formatTer(counter++));
                }
            } else if (!options.onlyExplicitTer) {
                if (!currRes || lastResidue.chainId !== currRes.chainId || currRes.hetflag) {
                    parts.push(lastResidue.formatTer(counter++));
                }
            }
        };

        for (const residue of this.residues) {
            maybeAddTer(residue);
            for (const atom of residue.atoms) {
                const index = counter++;
                parts.push(atom.format(index));
                atomMap.set(atom, index);
            }
            const terKey = this.chainInfo[residue.chainId]?.terKey;
            lastResidue = (!residue.hetflag || terKey) ? residue : null;
        }
        maybeAddTer();

        for (const residue of this.residues) {
            for (const atom of residue.atoms) {
                const index = atomMap.get(atom);
                const partners = atom.connections.map((conn) => this.lookupAtom(conn));
                if (partners.length > 0) {
                    const partnerIndices = partners.map((partner) => atomMap.get(partner))
                        .filter((ix) => ix != null);
                    parts.push(PdbData.formatConnection(index, partnerIndices));
                }
            }
        }
        return parts.join('\n');
    }

    static formatConnection(atomIndex, partnerIndices) {
        const indices = [atomIndex, ...partnerIndices]
            .map((index) => index.toString().padStart(5, ' '))
            .join('');
        return `CONECT${indices}`.padEnd(80, ' ');
    }

    /**
     * Make any corrections to inbound residues that are not supposed by pdb format.
     * Most notably: chain ids with more than one character
     * @param {PdbResidueSpec[]} residues
     * @return {{ residues: PdbResidueSpec[], chainMap: Map<string, string> }}
     */
    static fixResidues(residues) {
        const chainMap = PdbData.createChainMap(residues);
        const fixed = residues.map((residue) => ({
            ...residue,
            chainId: chainMap.get(residue.chainId),
        }));
        return {
            residues: fixed,
            chainMap,
        };
    }

    /**
     * Create a mapping of chain IDs to single-character IDs.
     * @param {PdbResidueSpec[]} residues
     * @return {Map<string, string>}
     */
    static createChainMap(residues) {
        const chainMap = new Map();
        const validChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const available = new Set(validChars.split(''));
        const replacingChainIds = new Set();
        const invalidPlaceholder = 'z';

        // Helper functions
        function mapTo(from, to) {
            chainMap.set(from, to);
            available.delete(to);
        }
        function getNextChainId() {
            for (const nextId of available) {
                return nextId;
            }
            return invalidPlaceholder; // No more valid chain IDs available
        }

        // Identify chains that aren't supported by pdb format
        for (const { chainId } of residues) {
            if (!chainId) mapTo(chainId, invalidPlaceholder);
            else if (chainId.length === 1) mapTo(chainId, chainId);
            else replacingChainIds.add(chainId);
        }

        for (const chainId of replacingChainIds) {
            mapTo(chainId, getNextChainId());
        }
        return chainMap;
    }
}

class PdbResidue {
    constructor(input) {
        this.atoms = [];
        this.altLocMap = new Map();
        if (input.recordName) {
            if (['ATOM', 'HETATM'].includes(input.recordName)) {
                this.hetflag = input.recordName === 'HETATM';
                this.resName = input.resName;
                this.seqNum = input.seqNum;
                this.iCode = input.iCode;
                this.chainId = input.chainId;
                this.resKey = input.resKey;
                this.addAtom(input);
            }
        } else {
            this.hetflag = input.hetflag || false;
            this.resName = input.resName;
            this.seqNum = input.seqNum;
            this.iCode = input.iCode || '';
            this.chainId = input.chainId;
            this.resKey = formResidueKey(this.resName, this.seqNum, this.chainId,
                { iCode: this.iCode });
            for (const atom of input.atoms) {
                this.addAtom(atom);
            }
        }
    }

    get hasH() { return this.atoms.some((atom) => atom.isH); }
    get isWater() { return this.resName === 'HOH'; }
    get isSingle() { return this.atoms.length === 1; }

    addAtom(rawAtom) {
        const atom = new PdbAtom(rawAtom, this);
        this.atoms.push(atom);
        if (!this.altLocMap.has(atom.altLoc)) {
            this.altLocMap.set(atom.altLoc, []);
        }
        this.altLocMap.get(atom.altLoc).push(atom);
    }

    getAtom({ atomNum, atomName, altLoc }) {
        if (atomNum != null) return this.atoms.find((atom) => atom.atomNum === atomNum);
        return this.atoms.find(
            (atom) => atom.atomName === atomName && (!altLoc || atom.altLoc === altLoc)
        );
    }

    toString() {
        let key = this.resKey;
        if (this.altLocMap.size > 1) {
            const altlocStr = [...this.altLocMap.entries()].map(([altLoc, list]) => `${altLoc || "''"}:${list.length}`).join(',');
            key += ` (${altlocStr})`;
        }

        return key;
    }

    formatTer(index) {
        const fields = [
            { name: 'recordName', value: 'TER   ', padding: 0 }, // left justified, so skip padding
            { name: 'index', value: index, padding: 5 },
            { name: 'blank', value: ' ', padding: 6 },
            { name: 'resName', value: this.resName, padding: 3 },
            { name: 'blank', value: ' ', padding: 1 },
            { name: 'chainId', value: this.chainId, padding: 1 },
            { name: 'seqNum', value: this.seqNum, padding: 4 },
            { name: 'iCode', value: this.iCode, padding: 1 },
        ];
        return fields.map(({ value, padding }) => value.toString().padStart(padding, ' ')).join('').padEnd(80);
    }
}

class PdbAtom {
    constructor(rawAtom, residue) {
        this.residue = residue;
        this.atomNum = rawAtom.atomNum;
        this.atomName = rawAtom.atomName;
        this.altLoc = rawAtom.altLoc;
        this.x = rawAtom.x;
        this.y = rawAtom.y;
        this.z = rawAtom.z;
        this.occupancy = rawAtom.occupancy;
        this.tempFactor = rawAtom.tempFactor;
        this.element = rawAtom.element;
        this.charge = rawAtom.charge;
        this.connections = rawAtom.connections || [];
    }

    get hetflag() { return this.residue.hetflag; }
    get resName() { return this.residue.resName; }
    get seqNum() { return this.residue.seqNum; }
    get iCode() { return this.residue.iCode; }
    get chainId() { return this.residue.chainId; }
    get resKey() { return this.residue.resKey; }
    get isH() { return this.element === 'H'; }

    toString() {
        return `${this.atomNum} ${this.resKey} ${this.atomName}`;
    }

    addConnection(connSpec) {
        if (!this.findConnection(connSpec)) {
            this.connections.push(connSpec);
        }
    }

    findConnection(connSpec) {
        return this.connections.find(({ resKey, atomName, altLoc }) => (
            resKey === connSpec.resKey && atomName === connSpec.atomName
            && (!connSpec.altLoc || altLoc === connSpec.altLoc)
        ));
    }

    format(atomNumOverride) {
        const {
            hetflag,
            atomNum, atomName, altLoc='', resName, chainId, seqNum, iCode='', x, y, z,
            occupancy=1.0, tempFactor=0.0, element, charge='',
        } = this;
        const fields = [
            { name: 'type', value: (hetflag) ? 'HETATM' : 'ATOM  ' },
            { name: 'atomNum', value: atomNumOverride ?? atomNum, padding: 5 },
            { name: 'blank', value: ' ', padding: 1 },
            {
                name: 'atomName',
                value: atomName,
                format(v) {
                    // atomName has weird justification rules:
                    // The element part is right justified in the first 2 columns,
                    // then the last two characters (ids) are left justified.
                    if (v.length === 4) return v;
                    return (element.length === 1) ? ` ${v}`.padEnd(4, ' ') : `${v}`.padEnd(4, ' ');
                },
            },
            { name: 'altLoc', value: altLoc, padding: 1 },
            { name: 'resName', value: resName, padding: 3 },
            { name: 'blank', value: ' ', padding: 1 },
            { name: 'chainId', value: chainId, padding: 1 },
            { name: 'seqNum', value: seqNum, padding: 4 },
            { name: 'iCode', value: iCode, padding: 1 },
            { name: 'blank', value: ' ', padding: 3 },
            { name: 'x', value: x.toFixed(3), padding: 8 },
            { name: 'y', value: y.toFixed(3), padding: 8 },
            { name: 'z', value: z.toFixed(3), padding: 8 },
            { name: 'occupancy', value: occupancy.toFixed(2), padding: 6 },
            { name: 'tempFactor', value: tempFactor.toFixed(2), padding: 6 },
            { name: 'blank', value: ' ', padding: 10 },
            { name: 'element', value: element, padding: 2 },
            { name: 'charge', value: charge, padding: 2 },
        ];
        const line = fields
            .map(({ value, padding, format }) => {
                if (format) return format(value);
                if (!padding) return value.toString();
                // Everything except for atomName is right justified
                return value.toString().padStart(padding, ' ');
            })
            .join('');
        return line;
    }
}

export function parsePdbAtomLine(input) {
    if (!input.startsWith('ATOM  ') && !input.startsWith('HETATM')) return null;
    const recordName = input.slice(0, 6).trim();
    const atomNum = Number(input.slice(6, 11).trim());
    const atomName = input.slice(12, 16).trim();
    const altLoc = input.slice(16, 17).trim();
    const resName = input.slice(17, 20).trim();
    const chainId = input.slice(21, 22).trim();
    const seqNum = Number(input.slice(22, 26).trim());
    const iCode = input.slice(26, 27).trim();
    const x = Number(input.slice(30, 38).trim());
    const y = Number(input.slice(38, 46).trim());
    const z = Number(input.slice(46, 54).trim());
    const occupancy = Number(input.slice(54, 60).trim());
    const tempFactor = Number(input.slice(60, 66).trim());
    const element = input.slice(76, 78).trim();
    const charge = input.slice(78, 80).trim();
    const resKey = formResidueKey(resName, seqNum, chainId, { iCode });
    return {
        recordName,
        atomNum,
        atomName,
        altLoc,
        resName,
        chainId,
        seqNum,
        iCode,
        x,
        y,
        z,
        occupancy,
        tempFactor,
        element,
        charge,
        resKey,
    };
}

export function parsePdbTerLine(input) {
    if (!input.startsWith('TER   ')) return null;
    const recordName = input.slice(0, 6).trim();
    const index = Number(input.slice(6, 11).trim());
    const resName = input.slice(17, 20).trim();
    const chainId = input.slice(21, 22).trim();
    const seqNum = Number(input.slice(22, 26).trim());
    const iCode = input.slice(26, 27).trim();
    const resKey = formResidueKey(resName, seqNum, chainId, { iCode });
    return {
        recordName,
        index,
        resName,
        chainId,
        seqNum,
        iCode,
        resKey,
    };
}

export function prasePdbConectLine(input) {
    if (!input.startsWith('CONECT')) return null;
    const recordName = input.slice(0, 6).trim();
    const atomNum = Number(input.slice(6, 11).trim());
    const others = [];
    for (let start = 11; start < 75; start += 5) {
        const conn = input.slice(start, start + 5).trim();
        if (conn) {
            others.push(Number(conn));
        }
    }
    return { recordName, atomNum, others };
}

// Regex instead of just using string indices
export function strictPdbAtomLineRegex() {
    // PDB atom line pattern:
    // eslint-disable-next-line
    // ATOM   9832  C   LYS A  41      31.199  21.649  -0.040  1.00 30.00           C  

    // HETATM
    // 12345678901234567890123456789012345678901234567890123456789012345678901234567890
    // 0        1         2         3         4         5         6         7         8
    const blank = (count=1) => ' '.repeat(count);
    const digitFor = (label, count) => `(?<${label}>[\\d ]{${count}})`;
    const alphanumFor = (label, count) => `(?<${label}>[A-Za-z0-9 ]{${count}})`;
    const anyFor = (label, count) => `(?<${label}>.{${count}})`;
    const real83 = /[\d -]{4}\.\d{3}/;
    const real83For = (label) => `(?<${label}>${real83.source})`;
    const real62 = /[\d -]{3}\.\d{2}/;
    const real62For = (label) => `(?<${label}>${real62.source})`;

    const recordName = '(?<recordName >ATOM {2}|HETATM)'; // 1-6
    const atomNum = digitFor('atomNum', 5); // 7-11
    // 12 is blank
    const atomName = anyFor('atomName', 4); // 13-16
    const altLoc = alphanumFor('altLoc', 1); // 17
    const resName = alphanumFor('resName', 3); // 18-20
    // 21 is blank
    const chainId = '(?<chainId>[A-Za-z0-9_ ])'; // 22
    const seqNum = digitFor('seqNum', 4); // 23-26
    const iCode = alphanumFor('iCode', 1); // 27
    // 28-30 is blank
    const xCoord = real83For('x'); // 31-38
    const yCoord = real83For('y'); // 39-46
    const zCoord = real83For('z'); // 47-54
    const occupancy = real62For('occupancy'); // 55-60
    const tempFactor = real62For('tempFactor'); // 61-66
    // 67-76 is blank
    const element = alphanumFor('element', 2); // 77-78
    const charge = '(?<charge>[ \\d+-]{2})'; // 79-80
    const atomLineRe = `^${recordName}${atomNum}${blank()}${atomName}${altLoc}${resName}${blank()}${chainId}${seqNum}${iCode}${blank(3)}${xCoord}${yCoord}${zCoord}${occupancy}${tempFactor}${blank(10)}${element}${charge}$`;
    return new RegExp(atomLineRe, 'gm');

    /*
    COLUMNS   Length   DATA  TYPE    FIELD        DEFINITION
    --------------------------------------------------------------------------------
    1  -  6    6       Record name   "ATOM  "
    7  - 11    5       Integer       serial       Atom  serial number.
    12         1       Blank         blank        Blank  space.
    13 - 16    4       Atom          name         Atom name.
    17         1       Character     altLoc       Alternate location indicator.
    18 - 20    3       Residue name  resName      Residue name.
    21         1       Blank         blank        Blank  space.
    22         1       Character     chainID      Chain identifier.
    23 - 26    4       Integer       resSeq       Residue sequence number.
    27         1       AChar         iCode        Code for insertion of residues.
    28 - 30    3       Blank         blank        Blank  space.
    31 - 38    8       Real(8.3)     x            Orthogonal coordinates for X in Angstroms.
    39 - 46    8       Real(8.3)     y            Orthogonal coordinates for Y in Angstroms.
    47 - 54    8       Real(8.3)     z            Orthogonal coordinates for Z in Angstroms.
    55 - 60    6       Real(6.2)     occupancy    Occupancy.
    61 - 66    6       Real(6.2)     tempFactor   Temperature  factor.
    67 - 76   10       Blank         blank        Blank  space.
    77 - 78    2       LString(2)    element      Element symbol, right-justified.
    79 - 80    2       LString(2)    charge       Charge  on the atom.
    */
}

function pdbAtomRawFromRe(match) {
    const ret = {
        recordName: match.groups.recordName.trim(),
        atomNum: Number(match.groups.atomNum.trim()),
        atomName: match.groups.atomName.trim(),
        altLoc: match.groups.altLoc.trim(),
        resName: match.groups.resName.trim(),
        chainId: match.groups.chainId.trim(),
        seqNum: Number(match.groups.seqNum.trim()),
        iCode: match.groups.iCode.trim(),
        x: Number(match.groups.x),
        y: Number(match.groups.y),
        z: Number(match.groups.z),
        occupancy: Number(match.groups.occupancy),
        tempFactor: Number(match.groups.tempFactor),
        element: match.groups.element.trim(),
        charge: match.groups.charge.trim(),
    };
    ret.resKey = formResidueKey(ret.resName, ret.seqNum, ret.chainId, { iCode: ret.iCode });
    return ret;
}

class PdbHeader {
    constructor(input={}) {
        this.remarks = input.remarks || { 10: 'BMaps Structure', 77: 'Generated by BMaps' };
    }

    format() {
        const parts = [
            PdbHeader.formatRemarks(this.remarks),
        ];
        return parts.join('\n');
    }

    static formatRemark(number, content) {
        function formatRemarkLine(num, value='') {
            return `REMARK ${num.toString().padStart(3, ' ')} ${value}`.padEnd(80);
        }
        if (typeof content === 'string') {
            return formatRemarkLine(number, content);
        } else if (Array.isArray(content)) {
            return ['', ...content].map((v) => formatRemarkLine(number, v)).join('\n');
        } else {
            return '';
        }
    }

    static formatRemarks(remarksObj) {
        return Object.entries(remarksObj)
            .map(([number, content]) => PdbHeader.formatRemark(number, content))
            .join('\n');
    }
}
